import {
  AnchoredPanel,
  AnchoredPanelType,
  getResourceLifecycleStatus,
  LabelMetaItem,
  MetaItemGroupsLayout,
  MetaItemsGroupsDirection,
  stateT,
  Status,
} from "o4a-react";
import * as React from "react";
import { Spinner, Stack } from "@fluentui/react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { metaItemLabelsGap, spinnerTestId, ttlOneMinCaching } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { getStatusInfo, optimizedRetryOption } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { AzureSubscriptionSummaryExt } from "../../hooks/useSubscriptions";
import { getAzureLocationName, getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export enum MetaItemSectionTestIds {
  ResourceGroup = "mis-resource-group",
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Status = "mis-status",
  Location = "mis-loaction",
  Subscription = "mis-subscription",
  SubscriptionId = "mis-subscription-id",
  DbVersion = "mis-db-version",
  DbHomeLocation = "mis-db-home-location",
}

export interface VmDbViewDbHomeProps extends OperationActionProps {
  dbHomeId: string | undefined;
  subscriptions: AzureSubscriptionSummaryExt[];
}

export const VmDbViewDbHome = ({
  targetId,
  dbHomeId,
  subscriptions,
  location,
  onCancel,
}: VmDbViewDbHomeProps): JSX.Element => {
  const idComps = parseId(dbHomeId);
  const resGroup = decodeURIComponent(idComps?.resourceGroup || "");
  const resourceName = decodeURIComponent(idComps?.resourceName || "");
  const subscription = subscriptions?.find(item => item.id === idComps?.subscriptionId);

  const { response, loading } = useQueryCall({
    wait: !dbHomeId,
    method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.getVmdbDbHome,
    options: {
      args: {
        subscriptionId: idComps?.subscriptionId || "",
        resourceGroupName: resGroup,
        dbHomeName: resourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadVMDbHome(),
      },
    },
  });

  const dbHome = response?.data;

  const essentialsMetaItemGroup: JSX.Element[] = [
    <Stack key="emig-01-left" tokens={{ childrenGap: metaItemLabelsGap }}>
      <LabelMetaItem testId={MetaItemSectionTestIds.ResourceGroup} label={Messages.labels.resourceGroup()}>
        <AzureResourceGroupLink resourceId={dbHomeId as string} />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.Status} label={Messages.labels.status()}>
        <Status
          iconPosition="right"
          label={stateT(dbHome?.status || "")}
          status={getResourceLifecycleStatus(dbHome?.status || "")}
          statusInfo={getStatusInfo(
            dbHome?.status || "",
            dbHome?.lastOperationStatus || "",
            dbHome?.lastOperationStatusDetails || "",
          )}
        />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.Location} label={Messages.labels.location()}>
        {getAzureLocationName(dbHome?.location || "")}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.Subscription} label={Messages.labels.subscription()}>
        <AzureSubscriptionLink resourceId={dbHomeId as string} subscriptionName={subscription?.name} />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.SubscriptionId} label={Messages.labels.subscriptionId()}>
        {idComps?.subscriptionId}
      </LabelMetaItem>
    </Stack>,
    <Stack key="emig-02-right" tokens={{ childrenGap: metaItemLabelsGap }}>
      <LabelMetaItem testId={MetaItemSectionTestIds.DbVersion} label={Messages.labels.databaseVersion()}>
        {dbHome?.dbVersion || ""}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.DbHomeLocation} label={Messages.labels.databaseHomeLocation()}>
        {dbHome?.dbHomeLocation || ""}
      </LabelMetaItem>
    </Stack>,
  ];

  return (
    <AnchoredPanel
      anchorId={targetId}
      type={AnchoredPanelType.CUSTOM_WIDTH}
      customWidth={700}
      title={dbHome?.name || ""}
      isOpen
      onClose={onCancel}
    >
      {loading ? <Spinner data-test-id={spinnerTestId} label={Messages.common.loading()} />
        : (
          <MetaItemGroupsLayout direction={MetaItemsGroupsDirection.Vertical}>
            { essentialsMetaItemGroup }
          </MetaItemGroupsLayout>
        )}
    </AnchoredPanel>
  );
};

export const newVmDbViewDbHome = (
  props: VmDbViewDbHomeProps,
): JSX.Element => (<VmDbViewDbHome {...props} />);
